import { apiRequest } from '../client'
import type {
  Category,
  CreateCategoryRequest,
  UnassignedFilter,
  UnassignedProductsResponse,
  UpdateCategoryRequest,
} from '../types'

export const categoriesApi = {
  /**
   * List all categories for the tenant
   * @returns List of categories sorted by sortOrder
   */
  list: () =>
    apiRequest<{ items: Array<Category> }>('/api/v0/admin/categories'),

  /**
   * Create a new category.
   * `parentId` set creates the group already nested under that main group — the server applies the
   * same two-level rules it applies to a re-parent, and refuses the whole create if they fail.
   * @param data Category data
   * @returns Success response with new category ID
   */
  create: (data: CreateCategoryRequest) =>
    apiRequest<{ ok: boolean; id: number }>('/api/v0/admin/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Rename, re-parent or reorder a category.
   * `parentId: null` removes the parent — it never means "leave unchanged".
   */
  update: (id: number, data: UpdateCategoryRequest) =>
    apiRequest<{ ok: boolean }>(`/api/v0/admin/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * Articles in the collector category worth assigning: sold in the period, or priced.
   * Paged because production holds thousands of articles that are neither.
   */
  unassigned: (params: {
    from?: string
    to?: string
    q?: string
    filter?: UnassignedFilter
    page?: number
    pageSize?: number
  }) => {
    const search = new URLSearchParams()
    if (params.from) search.set('from', params.from)
    if (params.to) search.set('to', params.to)
    if (params.q) search.set('q', params.q)
    if (params.filter) search.set('filter', params.filter)
    if (params.page) search.set('page', String(params.page))
    if (params.pageSize) search.set('pageSize', String(params.pageSize))
    const query = search.toString()
    return apiRequest<UnassignedProductsResponse>(
      `/api/v0/admin/categories/unassigned${query ? `?${query}` : ''}`,
    )
  },

  /** Moves several articles into one group in a single transaction — all of them, or none. */
  assign: (id: number, skus: Array<string>) =>
    apiRequest<{ ok: boolean; moved: number }>(
      `/api/v0/admin/categories/${id}/assign`,
      { method: 'POST', body: JSON.stringify({ skus }) },
    ),
}
